import Button from "@/components/Button";
import { Person } from "@/types";
import { useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
import { GET_PGM_PROGRAM_HISTORY_QUERY } from "./queries";
import { toFormat, isInFuture } from "@/lib/dateHelpers";
import TabLayout from "./_TabLayout";
import { MaterialNames } from "@/components/material_symbol_names";
import { useState } from "react";
import TransferModal from "./_components/TransferModal";
import { PGMProgramTable, type PGMMetric } from "@/features/person/components/PGMProgramTable";
import { buildPgmUniversityMetric as buildSharedPgmUniversityMetric } from "@/features/person/utils/pgm-university-metric";
import { DOMAIN, PERMISSION, useHasCapabilityBool } from "@/hooks/useCapabilities";

type PersonData = { person: Person };

const buildPgmUniversityMetric = (person: Person | undefined): PGMMetric | null => {
  if (!person) return null;
  const metric = buildSharedPgmUniversityMetric(person);
  return metric ? (metric as PGMMetric) : null;
};

const TOOLTIP_TITLE = "PGM Program Acceptable Progress";

const getActiveProgramMessage = (years: number | null): string => {
  const yearText = years ? `${years} years` : "the required time";
  return `Enrollee has ${yearText} to complete the Professional Golf Management Program from the date of purchase (Level 1 Start Date). If the program is not completed by the expiration, the enrollee will need to start over at the Qualifying Level to elect to membership.`;
};

const getExpiredProgramMessage = (years: number | null) => {
  const yearText = years ? `${years} years` : "the required time";
  return `Enrollee has not completed the Professional Golf Management Program within ${yearText} from the date of purchase (Level 1 Start Date). The enrollee must start over at the Qualifying Level to elect to membership.`;
};

const getExpirationDateMetric = (latestPgmProgram: Person["educationLatestPgmProgram"]) => {
  if (!latestPgmProgram?.pgmProgramCompleted) {
    const expirationYears = latestPgmProgram?.programDurationYears ?? null;
    return isInFuture(latestPgmProgram?.pgmProgramExpirationDate || "")
      ? { tooltip: { supportingText: getActiveProgramMessage(expirationYears), title: TOOLTIP_TITLE } }
      : {
          tooltip: { supportingText: getExpiredProgramMessage(expirationYears), title: TOOLTIP_TITLE },
          variant: "error" as const
        };
  }
};

export default function LearningDetails() {
  const { pgaId } = useParams();
  const [showTransferModal, setShowTransferModal] = useState(false);
  const canManageEducationRequirements = useHasCapabilityBool({
    domain: DOMAIN.EDUCATION,
    permission: PERMISSION.manageEducationPrograms
  });
  const { data, error, refetch } = useQuery<PersonData>(GET_PGM_PROGRAM_HISTORY_QUERY, {
    variables: { pgaId },
    fetchPolicy: "cache-first"
  });

  if (error) return <div>Error: {error.message}</div>;

  const personData = data?.person;
  const latestPgmProgram = personData?.educationLatestPgmProgram;
  const isCurrentPgmVersion = latestPgmProgram?.curriculumVersion === "3.1" && latestPgmProgram?.level === 1;

  const uniMetric = buildPgmUniversityMetric(personData);

  const metrics = [
    ...(uniMetric ? [uniMetric] : []),
    ...(latestPgmProgram
      ? [
          {
            label: "PGM Program Type",
            value: latestPgmProgram.programType,
            iconName: "school" as MaterialNames,
            link: {
              to: "/people/:pgaId/membership/path-to-membership",
              params: { pgaId: pgaId! }
            }
          },
          {
            label: "PGM Version",
            value: latestPgmProgram.curriculumVersion,
            iconName: "school" as MaterialNames,
            ...(isCurrentPgmVersion && {
              tooltip: {
                title: "Eligible for transfer to the Automated Facility Management Portfolio.",
                supportingText: "",
                actions: {
                  primary: <Button onClick={() => setShowTransferModal(true)}>Transfer to Automated Portfolio</Button>
                }
              }
            })
          },
          {
            label: "Completion Date",
            value: latestPgmProgram?.pgmProgramCompleted ? toFormat(latestPgmProgram.endDate || "") : null,
            iconName: "today" as MaterialNames,
            ...(latestPgmProgram?.pgmProgramCompleted && { variant: "success" as const })
          },
          {
            label: "Expiration Date",
            value: toFormat(latestPgmProgram?.pgmProgramExpirationDate || ""),
            iconName: "today" as MaterialNames,
            ...getExpirationDateMetric(latestPgmProgram)
          }
        ]
      : [])
  ] as PGMMetric[];

  return (
    <TabLayout>
      <PGMProgramTable
        key={`pgm-table-${pgaId}`}
        personId={pgaId || ""}
        tableHeading="PGA Professional Golf Management (PGM) History"
        actions={
          isCurrentPgmVersion
            ? [
                {
                  label: "Transfer to Automated Portfolio",
                  icon: "transfer_within_a_station" as MaterialNames,
                  variant: "text",
                  onClick: () => setShowTransferModal(true)
                }
              ]
            : []
        }
        tableBody="View PGM history including requirement statuses for each level below"
        testId="program-history-table"
        customMetrics={metrics}
        context="education"
        canManageEducationRequirements={canManageEducationRequirements}
      />
      {showTransferModal && (
        <TransferModal
          pgaId={pgaId!}
          personData={personData!}
          onClosed={() => setShowTransferModal(false)}
          refetch={refetch}
        />
      )}
    </TabLayout>
  );
}
